import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import Button from "components/Button";
import Input from "components/Input";
import Select from "components/Select";
import { JOIN_SUMMARY_PLACE_LIST } from "constants/haulpay";
import { routes } from "constants/routes";
import HistoryPaymentItem from "features/main/HaulPay/modules/Carrier/HistoryPaymentItem";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { UploadedFile } from "models/Component";
import { IOption } from "models/Form";
import { IUserInfoForm } from "models/User";
import { updateAccountAPI } from "services/api/user";
import { AppDispatch, RootState } from "store";
import { handleLoading } from "store/global/loader";
import { updateFactoringInfo } from "store/user/actions";
import styles from "./style.module.scss";

interface IProps {
  submitData?: any;
  onNext?: VoidFunction;
}

export default function JoinSummary({ submitData, onNext }: Readonly<IProps>) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const documents = Object.values(submitData.docs);

  const { userDetails } = useSelector((state: RootState) => state.userState);
  const isApplyHaulpay = pathname.startsWith("/finance");

  const {
    control,
    register,
    formState: { isSubmitting },
    handleSubmit
  } = useForm<IUserInfoForm & { referrer: IOption }>({
    defaultValues: { ...submitData, referrer: JOIN_SUMMARY_PLACE_LIST[0] }
  });

  const onSubmit = async ({ referrer }: { referrer: IOption }) => {
    const formData = {
      ...submitData,
      referrer: referrer.value,
      bank_info: null
    };

    const factoringInfo = {
      formData: formData,
      factoring_id: formData.factoring_id as string
    };

    if (formData?.factoring_id) {
      try {
        dispatch(handleLoading(true));
        await dispatch(updateFactoringInfo(factoringInfo));
        await updateAccountAPI(userDetails.id, formData.email, referrer.value);
        dispatch(handleLoading(false));
        showSuccessMessage("Successfully joined to HaulPay");
        if (isApplyHaulpay) {
          onNext?.();
        } else {
          goBack();
        }
      } catch {
        showErrorMessage("Error joining HaulPay");
      } finally {
        dispatch(handleLoading(false));
      }
    }
  };

  const goBack = () => {
    navigate(routes.HAULPAY_STATUS);
  };

  return (
    <form className={styles.summaryContainer} onSubmit={handleSubmit(onSubmit)}>
      <h3 className={styles.header}>Summary</h3>

      <div className={styles.fieldGroup}>
        <Controller
          name="referrer"
          control={control}
          render={({ field: { value = JOIN_SUMMARY_PLACE_LIST[0], onChange } }) => (
            <Select
              label="&nbsp;Where did you find us"
              options={JOIN_SUMMARY_PLACE_LIST}
              value={value}
              onChange={onChange}
            />
          )}
        />
        <Input
          name="mc_dot"
          register={register}
          type="number"
          inputMode="numeric"
          label="&nbsp;MC#"
          disabled
        />
      </div>

      <div className={styles.fieldGroup}>
        <Input
          name="owners_first_name"
          register={register}
          label="&nbsp;Owner's First Name"
          disabled
        />
        <Input
          name="owners_last_name"
          register={register}
          label="&nbsp;Owner's Last Name"
          disabled
        />
      </div>

      <div className={styles.fieldGroup}>
        <Input name="contact_phone" register={register} label="&nbsp;Owner's Phone#" disabled />
        <Input name="dba" register={register} label="&nbsp;Company Name" disabled />
      </div>

      <ul className={styles.uploadList}>
        {documents.map((document: UploadedFile) => (
          <HistoryPaymentItem key={crypto.randomUUID()} label={document.name} />
        ))}
      </ul>

      <div className={styles.footer}>
        <Button type="submit" size="lg" loading={isSubmitting}>
          SUBMIT
        </Button>
      </div>
    </form>
  );
}
